// GroVision 관리자 — 데이터 조회 (장비별 환경 데이터 · 표 + 그래프)
//
// 실데이터: fetchTimeSeries (차트 4종 + 장비 보유 센서) · fetchDevices · fetchFarms (TanStack Query).
// 날씨 카드 / AI 일일보고서는 MOCK (날씨 정적 데이터 + 로드된 시계열로 서술 생성).
import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { SensorType, SENSOR_CONFIGS, AnomalyLevel } from '@grosync/common';
import type { DeviceInfo, FarmInfo, SensorTimeSeries, WeatherSnapshot } from '@grosync/common';
import {
  AdminTopBar, ViewScroll, Panel, StatusPill, ModelTag, Sym, type DeviceStatus,
} from '../../ui';
import { fetchDevices } from '../../api/devices';
import { fetchFarms } from '../../api/farms';
import { fetchTimeSeries, fetchSensorData } from '../../api/sensors';
import { fetchWeather } from '../../api/weather';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import WeatherDetailModal from './WeatherDetailModal';
import { WeatherIcon } from './WeatherIcon';
import GrowthReportCard from './GrowthReportCard';
import { useDeviceModels } from '../device-models/useDeviceModels';
import {
  BUCKETS, BUCKET_INTERVAL, BUCKET_LABEL, periodRange, shiftAnchor, isLatestPeriod, periodLabel,
  bucketAxis, bucketTick, bucketFull, alignToAxis, type Bucket,
} from './period';

// ---------------------------------------------------------------------------
// 상수 / 타입
// ---------------------------------------------------------------------------

interface MetricDef {
  sensor: SensorType;
  label: string;
  unit: string;
  color: string;
  icon: string;
  /** 표시값으로 환산 (조도는 lux→klx, 그 외 항등) */
  toDisplay: (v: number) => number;
  /** 경고 여부 (표시값 기준) */
  warn: (v: number) => boolean;
  /** 소수 자리수 */
  decimals: number;
}

/** SENSOR_CONFIGS 임계값 기반 경고 판정 (표시값=원본값인 센서용) */
function warnFromConfig(sensor: SensorType): (v: number) => boolean {
  const t = SENSOR_CONFIGS[sensor].threshold;
  return (v) =>
    (t.warnHigh != null && v > t.warnHigh) ||
    (t.warnLow != null && v < t.warnLow) ||
    (t.criticalHigh != null && v > t.criticalHigh) ||
    (t.criticalLow != null && v < t.criticalLow);
}

/** 전체 센서 메트릭 정의 — 표시 순서 포함. 원본 데이터 표는 장비 보유 센서만 사용한다. */
const METRIC_LIST: readonly MetricDef[] = [
  { sensor: SensorType.TEMPERATURE, label: '온도', unit: '°C', color: '#E07B3E', icon: 'thermostat', toDisplay: (v) => v, warn: (v) => v > 30, decimals: 1 },
  { sensor: SensorType.HUMIDITY, label: '습도', unit: '%', color: '#2069C7', icon: 'water_drop', toDisplay: (v) => v, warn: (v) => v < 50, decimals: 0 },
  { sensor: SensorType.CO2, label: 'CO₂', unit: 'ppm', color: '#178A50', icon: 'co2', toDisplay: (v) => v, warn: (v) => v > 700, decimals: 0 },
  { sensor: SensorType.ILLUMINANCE, label: '조도', unit: 'klx', color: '#C77B20', icon: 'light_mode', toDisplay: (v) => v / 1000, warn: () => false, decimals: 1 },
  { sensor: SensorType.ATMOSPHERIC_PRESSURE, label: '기압', unit: 'hPa', color: '#6B7280', icon: 'compress', toDisplay: (v) => v, warn: warnFromConfig(SensorType.ATMOSPHERIC_PRESSURE), decimals: 0 },
  { sensor: SensorType.UV_INDEX, label: 'UV 지수', unit: 'UV', color: '#B45309', icon: 'wb_sunny', toDisplay: (v) => v, warn: warnFromConfig(SensorType.UV_INDEX), decimals: 1 },
  { sensor: SensorType.WIND_SPEED, label: '풍속', unit: 'm/s', color: '#0891B2', icon: 'air', toDisplay: (v) => v, warn: warnFromConfig(SensorType.WIND_SPEED), decimals: 1 },
  { sensor: SensorType.WIND_DIRECTION, label: '풍향', unit: '°', color: '#64748B', icon: 'navigation', toDisplay: (v) => v, warn: warnFromConfig(SensorType.WIND_DIRECTION), decimals: 0 },
  { sensor: SensorType.RAINFALL, label: '강우량', unit: 'mm', color: '#2563EB', icon: 'rainy', toDisplay: (v) => v, warn: warnFromConfig(SensorType.RAINFALL), decimals: 1 },
  { sensor: SensorType.SOIL_MOISTURE, label: '토양수분', unit: '%', color: '#0E7490', icon: 'humidity_high', toDisplay: (v) => v, warn: warnFromConfig(SensorType.SOIL_MOISTURE), decimals: 0 },
  { sensor: SensorType.SOIL_TEMPERATURE, label: '지온', unit: '°C', color: '#A16207', icon: 'device_thermostat', toDisplay: (v) => v, warn: warnFromConfig(SensorType.SOIL_TEMPERATURE), decimals: 1 },
  { sensor: SensorType.EC, label: 'EC', unit: 'mS/cm', color: '#7C3AED', icon: 'bolt', toDisplay: (v) => v, warn: warnFromConfig(SensorType.EC), decimals: 2 },
  { sensor: SensorType.PH, label: 'pH', unit: 'pH', color: '#DB2777', icon: 'science', toDisplay: (v) => v, warn: warnFromConfig(SensorType.PH), decimals: 1 },
  { sensor: SensorType.LEAF_WETNESS, label: '엽면수분', unit: '%', color: '#16A34A', icon: 'eco', toDisplay: (v) => v, warn: warnFromConfig(SensorType.LEAF_WETNESS), decimals: 0 },
];

const METRIC_DEFS = Object.fromEntries(METRIC_LIST.map((m) => [m.sensor, m])) as Record<SensorType, MetricDef>;

/** 미니카드·추이 차트에서 사용하는 기본 4개 지표 (요청에 따라 기존 유지) */
const CHART_SENSORS: readonly SensorType[] = [
  SensorType.TEMPERATURE, SensorType.HUMIDITY, SensorType.CO2, SensorType.ILLUMINANCE,
];

// MOCK: 정적 날씨 데이터 (gs-shared GS_WEATHER_NOW / GS_WEATHER_WEEK 포팅)
const WX_NOW = {
  city: '전북 김제시 백구면', temp: 23, code: 'partly_cloudy_day', desc: '구름조금',
  hi: 26, lo: 14, rain: 10,
} as const;
const WX_WEEK: readonly { d: string; code: string; hi: number; lo: number }[] = [
  { d: '오늘', code: 'partly_cloudy_day', hi: 26, lo: 14 },
  { d: '화', code: 'sunny', hi: 27, lo: 15 },
  { d: '수', code: 'cloudy', hi: 24, lo: 16 },
  { d: '목', code: 'rainy', hi: 21, lo: 14 },
  { d: '금', code: 'rainy', hi: 19, lo: 13 },
  { d: '토', code: 'cloudy', hi: 22, lo: 12 },
  { d: '일', code: 'sunny', hi: 25, lo: 13 },
];

// ---------------------------------------------------------------------------
// 헬퍼
// ---------------------------------------------------------------------------

function toUiStatus(s: DeviceInfo['status']): DeviceStatus {
  if (s === 'ACTIVE') return 'ok';
  if (s === 'WARNING' || s === 'ERROR') return 'warn';
  return 'off';
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

function fmtFullTime(iso: string, daily: boolean): string {
  const d = new Date(iso);
  if (daily) return `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())}`;
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** 원본 row용 — 초 단위까지 표시 */
function fmtFullTimeSec(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** 원본 차트 x축 라벨 — 개별 측정이라 분 단위까지 표시 (일별 기간은 날짜) */
function fmtTimeLabelRaw(iso: string, daily: boolean): string {
  const d = new Date(iso);
  if (daily) return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

interface AnomalyMeta { label: string; color: string }
/** 이상치 레벨 → 표시 라벨·색상 */
function anomalyMeta(level: AnomalyLevel): AnomalyMeta {
  if (level === AnomalyLevel.CRITICAL) return { label: '위험', color: 'var(--md-sys-color-error)' };
  if (level === AnomalyLevel.WARNING) return { label: '경고', color: 'var(--gs-warning)' };
  return { label: '정상', color: 'var(--md-sys-color-on-surface-variant)' };
}

function fmtMetric(v: number, decimals: number): string {
  return decimals > 0
    ? v.toFixed(decimals)
    : Math.round(v).toLocaleString('ko-KR');
}

interface Agg { avg: number; max: number; min: number; cur: number }
function aggregate(vals: number[]): Agg {
  if (!vals.length) return { avg: 0, max: 0, min: 0, cur: 0 };
  const sum = vals.reduce((a, b) => a + b, 0);
  return { avg: sum / vals.length, max: Math.max(...vals), min: Math.min(...vals), cur: vals[vals.length - 1] };
}

/** 차트 선택 토글 — 최대 2개. 가득 차면 가장 오래된 것을 밀어내고, 마지막 1개는 해제 불가 */
function toggleSelected(prev: SensorType[], s: SensorType): SensorType[] {
  if (prev.includes(s)) {
    return prev.length <= 1 ? prev : prev.filter((x) => x !== s);
  }
  if (prev.length >= 2) return [prev[prev.length - 1], s];
  return [...prev, s];
}

/** 시계열 → 표시값 배열 (시간순 정렬) */
function toDisplaySeries(series: SensorTimeSeries[] | undefined, def: MetricDef): { ts: string; v: number }[] {
  if (!series?.length) return [];
  return [...series]
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map((p) => ({ ts: p.timestamp, v: def.toDisplay(p.avg) }));
}

// ---------------------------------------------------------------------------
// 날씨 요약 카드 (MOCK)
// ---------------------------------------------------------------------------

/** 카드용 일별 항목 — 실데이터(WeatherDay)와 MOCK(WX_WEEK) 공통 부분만 사용 */
interface CardDay { d: string; code: string; hi: number; lo: number }

function WeatherCardAdmin({
  region, snapshot, onOpen,
}: {
  region?: string;
  snapshot: WeatherSnapshot | null;
  onOpen?: () => void;
}): React.ReactElement {
  const now = snapshot?.now ?? WX_NOW;
  const week: CardDay[] = snapshot?.week?.length ? snapshot.week : (WX_WEEK as readonly CardDay[]).slice();
  const clickable = !!(snapshot?.hourly && snapshot.hourly.length && onOpen);
  return (
    <div
      onClick={clickable ? onOpen : undefined}
      style={{
        borderRadius: 18, padding: '16px 18px', minHeight: 148,
        background: 'var(--md-sys-color-surface-container-lowest)',
        color: 'var(--md-sys-color-on-surface)', display: 'flex', flexDirection: 'column', gap: 12,
        border: '1px solid var(--md-sys-color-outline-variant)',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 9999, flexShrink: 0,
          // 하늘색 배경 — 흰 구름·주황 해 아이콘 대비 확보 (날씨 시각용 리터럴)
          background: 'linear-gradient(160deg, #7EC0FF 0%, #4F93E0 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <WeatherIcon code={now.code} size={36} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: '500 14px/18px var(--gs-font-kr)', opacity: 0.85, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Sym name="location_on" size={15} />{region || now.city}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 2 }}>
            <span className="gs-num" style={{ font: '800 46px/48px var(--gs-font-kr)', letterSpacing: '-1.5px' }}>{now.temp}°</span>
            <span style={{ font: '600 16px/20px var(--gs-font-kr)' }}>{now.desc}</span>
          </div>
        </div>
      </div>

      {/* 최고 · 최저 · 시간별 — 한 줄 배치 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        font: '500 14px/19px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Sym name="arrow_upward" size={15} fill={1} style={{ color: 'var(--gs-warning)' }} />최고
          <span className="gs-num" style={{ font: '700 16px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{now.hi}°</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Sym name="arrow_downward" size={15} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />최저
          <span className="gs-num" style={{ font: '700 16px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{now.lo}°</span>
        </span>
        <span style={{ flex: 1 }} />
        {clickable && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--md-sys-color-primary)', font: '600 13px/16px var(--gs-font-kr)' }}>
            <Sym name="schedule" size={15} fill={1} />시간별<Sym name="chevron_right" size={16} />
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        {week.slice(0, 7).map((d, i) => (
          <div key={`${d.d}-${i}`} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            padding: '6px 0', borderRadius: 10, background: i === 0 ? 'var(--md-sys-color-secondary-container)' : 'transparent',
          }}>
            <span style={{ font: '600 12px/15px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{d.d}</span>
            <WeatherIcon code={d.code} size={22} />
            <span className="gs-num" style={{ font: '700 15px/19px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{d.hi}°</span>
            <span className="gs-num" style={{ font: '500 13px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{d.lo}°</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI 일일보고서 카드 (MOCK — 로드된 시계열로 서술 생성)
// ---------------------------------------------------------------------------

interface ReportInput {
  online: boolean;
  farmName: string;
  /** 표시값 배열 (지표별) */
  temp: number[];
  hum: number[];
  co2: number[];
}

function AIDailyReport({
  device, farmName, input,
}: { device: DeviceInfo; farmName: string; input: ReportInput | null }): React.ReactElement {
  const offline = !input || !input.online;
  const t = input && input.temp.length ? aggregate(input.temp) : null;
  const h = input && input.hum.length ? aggregate(input.hum) : null;
  const c = input && input.co2.length ? aggregate(input.co2) : null;
  const noData = !t || !h || !c;
  const warnT = input ? input.temp.filter((v) => v > 30).length : 0;
  const warnH = input ? input.hum.filter((v) => v < 50).length : 0;
  const warnC = input ? input.co2.filter((v) => v > 700).length : 0;
  const anomalies = warnT + warnH + warnC;

  const now = new Date();
  const stamp = `${now.getFullYear()}.${pad2(now.getMonth() + 1)}.${pad2(now.getDate())} ${pad2(now.getHours())}:00 기준`;

  let body: string;
  if (noData) {
    body = offline
      ? `${device.name} 장비가 오프라인 상태입니다. 선택한 기간에 수집된 환경 데이터가 없습니다. 통신 복구 후 일일 분석이 자동 재개됩니다.`
      : `${device.name}의 선택한 기간에 수집된 환경 데이터가 없습니다.`;
  } else if (anomalies === 0) {
    body = `오늘 ${farmName}의 환경은 전반적으로 안정적입니다. 평균 온도 ${t.avg.toFixed(1)}°C · 습도 ${Math.round(h.avg)}% · CO₂ ${Math.round(c.avg)}ppm으로 권장 범위를 유지했으며, 현재까지 이상 징후는 감지되지 않았습니다.`;
  } else {
    const parts = [
      warnT ? `고온 ${warnT}회` : '',
      warnH ? `저습 ${warnH}회` : '',
      warnC ? `CO₂ 초과 ${warnC}회` : '',
    ].filter(Boolean).join(' · ');
    body = `오늘 ${farmName}에서 ${anomalies}건의 환경 이상이 감지되었습니다. ${parts}. 평균 온도 ${t.avg.toFixed(1)}°C · 습도 ${Math.round(h.avg)}%로, 환기·관수 점검을 권장합니다.`;
  }

  return (
    <div style={{
      borderRadius: 18, padding: '16px 18px', minHeight: 148, position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(135deg, var(--md-sys-color-secondary-container) 0%, var(--md-sys-color-primary-container) 100%)',
      color: 'var(--md-sys-color-on-secondary-container)',
      border: '1px solid var(--md-sys-color-outline-variant)', display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sym name="auto_awesome" size={20} fill={1} style={{ color: 'var(--md-sys-color-secondary)' }} />
        <span style={{ font: '700 15px/20px var(--gs-font-kr)' }}>AI 일일보고서</span>
        <span className="gs-tag" style={{ background: 'rgba(0,0,0,0.08)', color: 'var(--md-sys-color-on-secondary-container)', height: 18, fontSize: 10 }}>BETA</span>
        <span style={{ flex: 1 }} />
        <span className="gs-num" style={{ font: '400 11px/14px var(--gs-font-kr)', opacity: 0.75 }}>{stamp}</span>
      </div>
      <div style={{ font: '500 13px/14px var(--gs-font-kr)', opacity: 0.85, display: 'flex', alignItems: 'center', gap: 5 }}>
        <Sym name="sensors" size={13} />{device.name} · {device.id.slice(0, 8)}
      </div>
      <div style={{ font: '400 13px/20px var(--gs-font-kr)', textWrap: 'pretty' }}>{body}</div>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {noData ? (
          <RepChip icon="cloud_off" label="데이터 없음" />
        ) : (
          <>
            <RepChip icon="thermostat" label={`평균 ${t.avg.toFixed(1)}°C`} />
            <RepChip icon="water_drop" label={`습도 ${Math.round(h.avg)}%`} />
            <RepChip icon="co2" label={`CO₂ ${Math.round(c.avg)}ppm`} />
            <RepChip icon={anomalies ? 'warning' : 'check_circle'} label={anomalies ? `이상 ${anomalies}건` : '이상 0건'} warn={anomalies > 0} />
          </>
        )}
      </div>
    </div>
  );
}

function RepChip({ icon, label, warn }: { icon: string; label: string; warn?: boolean }): React.ReactElement {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 11px', borderRadius: 9999,
      background: warn ? 'var(--gs-warning-container)' : 'rgba(255,255,255,0.55)',
      color: warn ? 'var(--gs-on-warning-container)' : 'var(--md-sys-color-on-secondary-container)',
      font: '600 11px/15px var(--gs-font-kr)',
    }}>
      <Sym name={icon} size={13} fill={1} style={{ color: warn ? 'var(--gs-warning)' : 'inherit' }} />{label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 차트 (recharts) — 영역 그라데이션 + 라인
// ---------------------------------------------------------------------------

/** 차트 시리즈 정의 — 최대 2개를 좌/우 축에 겹쳐 표시 */
interface ChartSeries {
  /** 병합 row의 dataKey (v0 / v1) */
  key: string;
  color: string;
  unit: string;
  label: string;
  /** 좌측(첫 번째) / 우측(두 번째) Y축 */
  axis: 'left' | 'right';
}

function MetricChart({
  rows, series, tickFmt, labelFmt, breakGaps = false,
}: {
  rows: Array<Record<string, number | string>>;
  series: ChartSeries[];
  tickFmt: (iso: string) => string;
  labelFmt: (iso: string) => string;
  /** true면 값 없는 버킷에서 선을 끊는다 (통계 모드 — 결측을 직선으로 메우지 않음) */
  breakGaps?: boolean;
}): React.ReactElement {
  // 단일 시리즈일 때만 영역 그라데이션 — 2개 겹치면 면이 탁해져 라인만 표시
  const showArea = series.length === 1;
  const hasRight = series.some((s) => s.axis === 'right');
  const yTick = { fontSize: 11, fill: 'var(--md-sys-color-on-surface-variant)', fontFamily: 'var(--gs-font-kr)' } as const;
  const fmtNum = (v: number): string => v.toLocaleString('ko-KR');
  return (
    <ResponsiveContainer width="100%" height={440}>
      <ComposedChart data={rows} margin={{ top: 18, right: 18, bottom: 6, left: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`grad_${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid vertical={false} stroke="var(--md-sys-color-outline-variant)" strokeDasharray="3 4" />
        <XAxis
          dataKey="ts"
          tickFormatter={(v: string) => tickFmt(v)}
          tick={{ fontSize: 11, fill: 'var(--md-sys-color-on-surface-variant)', fontFamily: 'var(--gs-font-kr)' }}
          tickLine={false}
          axisLine={false}
          minTickGap={24}
        />
        <YAxis
          yAxisId="left"
          tick={yTick}
          tickLine={false}
          axisLine={false}
          width={44}
          tickFormatter={fmtNum}
          stroke={series[0]?.color}
        />
        {hasRight && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={yTick}
            tickLine={false}
            axisLine={false}
            width={44}
            tickFormatter={fmtNum}
            stroke={series.find((s) => s.axis === 'right')?.color}
          />
        )}
        <Tooltip
          contentStyle={{
            background: 'var(--md-sys-color-surface-container-lowest)',
            border: '1px solid var(--md-sys-color-outline-variant)',
            borderRadius: 12, fontSize: 12, fontFamily: 'var(--gs-font-kr)',
          }}
          labelFormatter={(v) => labelFmt(String(v))}
          formatter={(value, name) => {
            const s = series.find((x) => x.label === name);
            return [`${Number(value).toLocaleString('ko-KR')} ${s?.unit ?? ''}`, name];
          }}
        />
        {series.map((s) => (
          <React.Fragment key={s.key}>
            {showArea && (
              <Area yAxisId={s.axis} type="monotone" dataKey={s.key} stroke="none" fill={`url(#grad_${s.key})`} connectNulls={!breakGaps} />
            )}
            <Line
              yAxisId={s.axis} type="monotone" dataKey={s.key} name={s.label} stroke={s.color}
              strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" connectNulls={!breakGaps}
              // 선을 끊는 모드에선 앞뒤가 빈 단독 버킷이 선분 없이 사라지므로 점으로 표시
              dot={breakGaps ? { r: 2.5, fill: s.color, strokeWidth: 0 } : false}
              activeDot={{ r: 5, fill: s.color, stroke: 'var(--md-sys-color-surface-container-lowest)', strokeWidth: 2 }}
            />
          </React.Fragment>
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** 기간 이동 — ◀ 기간 ▶ + 현재 기간 복귀. 현재 기간이면 다음·복귀 비활성 */
function PeriodNav({
  label, atLatest, onPrev, onNext, onToday,
}: {
  label: string;
  atLatest: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}): React.ReactElement {
  const iconBtn = (disabled: boolean): React.CSSProperties => ({
    width: 34, height: 34, borderRadius: 9999, border: 0, display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', background: 'transparent',
    cursor: disabled ? 'default' : 'pointer',
    color: disabled ? 'var(--md-sys-color-outline)' : 'var(--md-sys-color-on-surface)',
  });
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 2, padding: 4, borderRadius: 9999,
      background: 'var(--md-sys-color-surface-container-high)',
    }}>
      <button onClick={onPrev} aria-label="이전 기간" style={iconBtn(false)}>
        <Sym name="chevron_left" size={20} />
      </button>
      <span style={{
        minWidth: 150, textAlign: 'center', font: '600 13px/1 var(--gs-font-kr)',
        color: 'var(--md-sys-color-on-surface)', fontVariantNumeric: 'tabular-nums',
      }}>
        {label}
      </span>
      <button onClick={onNext} disabled={atLatest} aria-label="다음 기간" style={iconBtn(atLatest)}>
        <Sym name="chevron_right" size={20} />
      </button>
      <button
        onClick={onToday}
        disabled={atLatest}
        style={{
          height: 34, padding: '0 14px', borderRadius: 9999, border: 0,
          cursor: atLatest ? 'default' : 'pointer',
          background: atLatest ? 'transparent' : 'var(--md-sys-color-surface-container-lowest)',
          color: atLatest ? 'var(--md-sys-color-outline)' : 'var(--md-sys-color-primary)',
          font: '600 13px/1 var(--gs-font-kr)',
        }}
      >
        오늘
      </button>
    </div>
  );
}

/** 통계/원본 세그먼트 토글 — 기간 선택 옆에 위치, 차트·원본 데이터 표에 동시 적용. 기간 세그먼트와 동일 사이즈 */
function AggRawToggle({
  mode, onChange,
}: { mode: 'agg' | 'raw'; onChange: (m: 'agg' | 'raw') => void }): React.ReactElement {
  return (
    <div style={{ display: 'inline-flex', padding: 4, borderRadius: 9999, background: 'var(--md-sys-color-surface-container-high)' }}>
      {([['agg', '통계'], ['raw', '원본']] as const).map(([key, label]) => {
        const active = mode === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              height: 34, padding: '0 18px', borderRadius: 9999, border: 0, cursor: 'pointer',
              background: active ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
              color: active ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
              font: `${active ? 700 : 500} 13px/1 var(--gs-font-kr)`,
              boxShadow: active ? 'var(--md-sys-elevation-1)' : 'none',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** 통계 한 줄 — 좌측 라벨 / 우측 값. 그래프 좌측 세로 통계 카드에서 사용 */
function StatLine({
  label, value, unit, big,
}: { label: string; value: string; unit: string; big?: boolean }): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{label}</span>
      <span className="gs-num" style={{ font: `700 ${big ? 19 : 15}px/22px var(--gs-font-kr)`, letterSpacing: '-0.3px', color: 'var(--md-sys-color-on-surface)' }}>
        {value}<span style={{ font: '500 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginLeft: 2 }}>{unit}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 메인 뷰
// ---------------------------------------------------------------------------

/** 원본 데이터 와이드 표의 한 행 — 한 측정 시각의 센서별 값·이상치 */
interface RawWideRow {
  ts: string;
  cells: Partial<Record<SensorType, { v: number; anomaly: AnomalyLevel }>>;
}

export default function DataView(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const initDeviceId = searchParams.get('deviceId') ?? '';

  const [devId, setDevId] = useState<string>(initDeviceId);
  // 통계 단위 + 앵커(보고 있는 기간 안의 한 시각). 기간은 KST 달력 기준 (period.ts)
  const [bucket, setBucket] = useState<Bucket>('hour');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  // 차트에 겹쳐 볼 지표 (최대 2개, 좌/우 축). 장비 변경 시 보유 센서로 보정.
  const [selected, setSelected] = useState<SensorType[]>([SensorType.TEMPERATURE, SensorType.HUMIDITY]);
  // 데이터 표시 모드: 'agg'=시간버킷 통계 평균 / 'raw'=개별 측정값. 차트·원본 데이터 표에 동시 적용.
  const [dataMode, setDataMode] = useState<'agg' | 'raw'>('agg');
  const [rawPage, setRawPage] = useState<number>(1);
  const [aggPage, setAggPage] = useState<number>(1);
  const [weatherOpen, setWeatherOpen] = useState<boolean>(false);

  // URL 파라미터 소비 후 제거 (deep-link 1회성)
  useEffect(() => {
    if (initDeviceId) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const devicesQ = useQuery<DeviceInfo[]>({ queryKey: ['devices'], queryFn: fetchDevices });
  const farmsQ = useQuery<FarmInfo[]>({ queryKey: ['farms'], queryFn: fetchFarms });

  const devices = useMemo(() => devicesQ.data ?? [], [devicesQ.data]);
  const farms = useMemo(() => farmsQ.data ?? [], [farmsQ.data]);

  // 기본 장비 = 첫 온라인 장비
  const defaultDeviceId = useMemo(() => {
    const online = devices.find((d) => toUiStatus(d.status) !== 'off');
    return (online ?? devices[0])?.id ?? '';
  }, [devices]);

  // initDeviceId 가 유효하지 않거나 비었으면 기본값으로
  const effectiveDevId = useMemo(() => {
    if (devId && devices.some((d) => d.id === devId)) return devId;
    return defaultDeviceId;
  }, [devId, devices, defaultDeviceId]);

  useEffect(() => {
    if (!devId && defaultDeviceId) setDevId(defaultDeviceId);
  }, [devId, defaultDeviceId]);

  const device = devices.find((d) => d.id === effectiveDevId) ?? null;
  const uiStatus = device ? toUiStatus(device.status) : 'off';
  const isOff = uiStatus === 'off';
  const range = useMemo(() => periodRange(bucket, anchor), [bucket, anchor]);
  const interval = BUCKET_INTERVAL[bucket];
  const atLatest = isLatestPeriod(bucket, anchor);

  // 농장 / 지역 표시
  const farm = device?.farmId ? farms.find((f) => f.id === device.farmId) ?? null : null;
  const farmName = farm?.name ?? '미할당 농장';
  const region = device?.location?.description ?? farm?.location?.description ?? WX_NOW.city;

  // 날씨 조회 좌표 = 장비 위치 → 농장 위치 순. 둘 다 없으면 조회 비활성(카드는 placeholder).
  const coords = device?.location ?? farm?.location ?? null;
  const weatherQ = useQuery<WeatherSnapshot>({
    queryKey: ['weather', coords?.lat, coords?.lng],
    queryFn: () => fetchWeather(coords!.lat, coords!.lng, region),
    enabled: !!coords,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  const weather = weatherQ.data ?? null;

  // 생육보고서 카드 표시 여부 — 장비모델이 growthEnabled 일 때만.
  // 서버 장비모델을 device_model_id(=code) 기준으로 조회한다.
  const { models: deviceModels } = useDeviceModels();
  const growthEnabled = useMemo(() => {
    const m = deviceModels.find((x) => x.code === device?.deviceModelId);
    return m?.growthEnabled ?? false;
  }, [deviceModels, device?.deviceModelId]);
  const showGrowth = !!device && growthEnabled;

  // ── 시계열 조회 (차트 기본 4종 ∪ 장비 보유 센서, 중복 제거) ──
  const fromIso = range.from.toISOString();
  // 서버는 time <= to 로 조회 — 다음 기간 첫 시각이 끼지 않도록 1ms 앞까지
  const toIso = new Date(range.to.getTime() - 1).toISOString();
  const periodKey = `${bucket}:${fromIso}`;
  // 오프라인이어도 과거 측정 이력은 조회한다 (online 여부는 데이터 표시를 막지 않음)
  const enabled = !!device;

  // 조회 대상 센서 = 차트 기본 4종 + 선택 장비가 보유한 센서
  const querySensors = useMemo<SensorType[]>(() => {
    const set = new Set<SensorType>(CHART_SENSORS);
    for (const s of device?.sensors ?? []) if (METRIC_DEFS[s]) set.add(s);
    return [...set];
  }, [device]);

  const seriesQueries = useQueries({
    queries: querySensors.map((s) => ({
      queryKey: ['timeseries', effectiveDevId, periodKey, s],
      queryFn: () => fetchTimeSeries({
        deviceId: effectiveDevId,
        sensorType: s,
        from: fromIso,
        to: toIso,
        interval,
      }),
      enabled,
    })),
  });

  // 센서별 표시 시계열 매핑
  const displayBySensor = useMemo(() => {
    const out = {} as Record<SensorType, { ts: string; v: number }[]>;
    querySensors.forEach((s, i) => {
      out[s] = toDisplaySeries(seriesQueries[i].data, METRIC_DEFS[s]);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesQueries.map((q) => q.data).join('|'), querySensors, periodKey]);

  const seriesLoading = enabled && seriesQueries.some((q) => q.isLoading);
  const seriesError = seriesQueries.find((q) => q.isError)?.error;

  // 원본 데이터 표 컬럼 = 장비 보유 센서(정의 순서). 없으면 기본 4종 폴백
  const tableSensors = useMemo<SensorType[]>(() => {
    const owned = (device?.sensors ?? []).filter((s) => METRIC_DEFS[s]);
    const ordered = METRIC_LIST.map((m) => m.sensor).filter((s) => owned.includes(s));
    return ordered.length ? ordered : [...CHART_SENSORS];
  }, [device]);

  // 원본 데이터 표 행 (타임스탬프 병합)
  const tableRows = useMemo(() => {
    const map = new Map<string, { ts: string } & Partial<Record<SensorType, number>>>();
    for (const s of tableSensors) {
      for (const p of displayBySensor[s] ?? []) {
        const row = map.get(p.ts) ?? { ts: p.ts };
        row[s] = p.v;
        map.set(p.ts, row);
      }
    }
    return Array.from(map.values()).sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
  }, [displayBySensor, tableSensors]);

  // 표 그리드: 시각 + 센서별 컬럼. 컬럼이 많으면 가로 스크롤되도록 최소폭 확보
  const tableGridCols = `minmax(150px, 1.2fr) ${tableSensors.map(() => 'minmax(96px, 1fr)').join(' ')}`;
  const tableMinWidth = 150 + tableSensors.length * 112;

  // 통계 표 클라이언트 페이지네이션 — 데이터가 많을 때 페이지로 분할
  const aggTotalPages = Math.max(1, Math.ceil(tableRows.length / AGG_PAGE_SIZE));
  const safeAggPage = Math.min(aggPage, aggTotalPages);
  const pagedTableRows = useMemo(
    () => tableRows.slice((safeAggPage - 1) * AGG_PAGE_SIZE, safeAggPage * AGG_PAGE_SIZE),
    [tableRows, safeAggPage],
  );

  // ── 원본(raw) 데이터 조회 — 개별 측정 row (raw 모드일 때만) ──
  const RAW_PAGE_SIZE = 100;
  const rawQ = useQuery({
    queryKey: ['sensordata-raw', effectiveDevId, periodKey, rawPage],
    queryFn: () => fetchSensorData({
      deviceId: effectiveDevId,
      from: fromIso,
      to: toIso,
      page: rawPage,
      pageSize: RAW_PAGE_SIZE,
    }),
    enabled: enabled && dataMode === 'raw',
  });
  const rawRows = rawQ.data?.items ?? [];
  const rawTotal = rawQ.data?.total ?? 0;
  const rawTotalPages = Math.max(1, Math.ceil(rawTotal / RAW_PAGE_SIZE));

  // 원본 측정값을 초 단위 시각 기준 와이드 행으로 피벗 — 통계 표와 동일 레이아웃(한 줄에 전체 센서).
  // 같은 초에 들어온 측정은 한 줄로 묶는다(실제 번들은 모든 센서가 같은 ts라 그대로 묶임).
  // rawRows는 최신순(DESC)이라 같은 초·같은 센서가 중복되면 가장 최근 값을 유지한다. 최신순 정렬.
  const rawWideRows = useMemo<RawWideRow[]>(() => {
    const map = new Map<string, RawWideRow>();
    for (const r of rawRows) {
      if (!METRIC_DEFS[r.sensorType]) continue;
      const d = new Date(r.timestamp);
      d.setMilliseconds(0);
      const sec = d.toISOString(); // 초 단위 버킷 키 (ms 절삭)
      const row = map.get(sec) ?? { ts: sec, cells: {} };
      if (row.cells[r.sensorType] == null) {
        row.cells[r.sensorType] = { v: METRIC_DEFS[r.sensorType].toDisplay(r.value), anomaly: r.anomaly };
      }
      map.set(sec, row);
    }
    return Array.from(map.values()).sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  }, [rawRows]);

  // 장비·기간 변경 시 raw 페이지 초기화
  useEffect(() => {
    setRawPage(1);
    setAggPage(1);
  }, [effectiveDevId, periodKey]);

  // ── 차트 선택 보정 — 장비 변경 시 보유 센서 앞 2개로 리셋. 같은 장비에서 유효하면 사용자 선택 유지 ──
  useEffect(() => {
    setSelected((prev) => {
      const valid = prev.filter((s) => tableSensors.includes(s));
      return valid.length === prev.length && valid.length > 0 ? prev : tableSensors.slice(0, 2);
    });
  }, [tableSensors]);

  // ── 차트 원본 데이터 조회 — 선택 지표별 개별 측정값 (raw 모드일 때만) ──
  const chartRawQueries = useQueries({
    queries: selected.map((s) => ({
      queryKey: ['chartraw', effectiveDevId, periodKey, s],
      queryFn: () => fetchSensorData({
        deviceId: effectiveDevId,
        sensorType: s,
        from: fromIso,
        to: toIso,
        page: 1,
        pageSize: CHART_RAW_PAGE_SIZE,
      }),
      enabled: enabled && dataMode === 'raw',
    })),
  });

  // 선택 지표별 표시 시계열 (통계 또는 원본)
  const chartSeriesData = useMemo<Partial<Record<SensorType, { ts: string; v: number }[]>>>(() => {
    const out: Partial<Record<SensorType, { ts: string; v: number }[]>> = {};
    selected.forEach((s, i) => {
      if (dataMode === 'raw') {
        const items = chartRawQueries[i]?.data?.items ?? [];
        out[s] = [...items]
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          .map((r) => ({ ts: r.timestamp, v: METRIC_DEFS[s].toDisplay(r.value) }));
      } else {
        out[s] = displayBySensor[s] ?? [];
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, dataMode, displayBySensor, chartRawQueries.map((q) => q.dataUpdatedAt).join(',')]);

  // 차트 시리즈 정의 (첫 번째=좌축 / 두 번째=우축)
  const chartSeries = useMemo<ChartSeries[]>(
    () => selected.map((s, i) => ({
      key: `v${i}`,
      color: METRIC_DEFS[s].color,
      unit: METRIC_DEFS[s].unit,
      label: METRIC_DEFS[s].label,
      axis: i === 0 ? 'left' : 'right',
    })),
    [selected],
  );

  // 타임스탬프 병합 row — 두 시리즈를 한 차트에 겹쳐 표시 (번들이라 ts 공유)
  const chartRows = useMemo(() => {
    const map = new Map<string, Record<string, number | string>>();
    selected.forEach((s, i) => {
      for (const p of chartSeriesData[s] ?? []) {
        const row = map.get(p.ts) ?? { ts: p.ts };
        row[`v${i}`] = p.v;
        map.set(p.ts, row);
      }
    });
    const sorted = Array.from(map.values()).sort(
      (a, b) => new Date(a.ts as string).getTime() - new Date(b.ts as string).getTime(),
    );
    // 통계 모드는 기간 전체 버킷 축에 맞춰 편다(결측 버킷이 자리를 차지하고 선이 끊김).
    // 원본 모드는 버킷 격자가 없고 측정 시각이 불규칙해 그대로 둔다.
    return dataMode === 'agg' ? alignToAxis(sorted, bucketAxis(bucket, range)) : sorted;
  }, [selected, chartSeriesData, dataMode, bucket, range]);

  // 통계 모드는 x축을 기간 전체로 깔아 row 가 항상 있으므로, 실제 값 유무로 빈 상태를 판정한다
  const chartHasData = selected.some((s) => (chartSeriesData[s] ?? []).length > 0);

  const chartLoading = dataMode === 'raw'
    ? (enabled && chartRawQueries.some((q) => q.isLoading))
    : seriesLoading;

  // AI 보고서 입력 — 로드된 데이터로 구성 (online 여부는 플래그로만 전달)
  const reportInput: ReportInput | null = !device ? null : {
    online: !isOff,
    farmName,
    temp: (displayBySensor[SensorType.TEMPERATURE] ?? []).map((d) => d.v),
    hum: (displayBySensor[SensorType.HUMIDITY] ?? []).map((d) => d.v),
    co2: (displayBySensor[SensorType.CO2] ?? []).map((d) => d.v),
  };

  // CSV 내보내기 (Blob + BOM) — 현재 보고 있는 표(통계/원본) 기준
  const exportCSV = (): void => {
    if (!device) return;
    let header: string;
    let lines: string[];
    if (dataMode === 'raw') {
      if (!rawWideRows.length) return;
      header = ['시각', ...tableSensors.map((s) => `${METRIC_DEFS[s].label}(${METRIC_DEFS[s].unit})`)].join(',');
      lines = rawWideRows.map((r) => [
        fmtFullTimeSec(r.ts),
        ...tableSensors.map((s) => {
          const cell = r.cells[s];
          return cell == null ? '' : fmtMetric(cell.v, METRIC_DEFS[s].decimals).replace(/,/g, '');
        }),
      ].join(','));
    } else {
      if (!tableRows.length) return;
      header = ['시각', ...tableSensors.map((s) => `${METRIC_DEFS[s].label}(${METRIC_DEFS[s].unit})`)].join(',');
      lines = tableRows.map((r) => [
        bucketFull(bucket, r.ts),
        ...tableSensors.map((s) => {
          const v = r[s];
          return v == null ? '' : fmtMetric(v, METRIC_DEFS[s].decimals).replace(/,/g, '');
        }),
      ].join(','));
    }
    const csv = [header, ...lines].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${device.id.slice(0, 8)}_${bucket}_${periodLabel(bucket, anchor).replace(/[^0-9]+/g, '')}_${dataMode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const csvDisabled = dataMode === 'raw' ? !rawWideRows.length : !tableRows.length;

  // ── 로딩 / 에러 (장비·농장 목록) ──
  if (devicesQ.isLoading || farmsQ.isLoading) {
    return (
      <>
        <AdminTopBar title="데이터 조회" subtitle="장비별 환경 데이터 · 표 · 그래프" />
        <ViewScroll>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><LoadingSpinner /></div>
        </ViewScroll>
      </>
    );
  }
  if (devicesQ.isError) {
    return (
      <>
        <AdminTopBar title="데이터 조회" subtitle="장비별 환경 데이터 · 표 · 그래프" />
        <ViewScroll><PageError message="장비 목록을 불러오지 못했습니다." /></ViewScroll>
      </>
    );
  }

  return (
    <>
      <AdminTopBar
        title="데이터 조회"
        subtitle="장비별 환경 데이터 · 표 · 그래프"
      />

      <ViewScroll gap={18}>
        {/* 날씨 · AI 일일보고서 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: showGrowth
            ? 'minmax(260px, 1fr) minmax(0, 1.1fr) minmax(0, 1fr)'
            : 'minmax(320px, 0.85fr) minmax(0, 1.15fr)',
          gap: 16,
        }}>
          <WeatherCardAdmin region={region} snapshot={weather} onOpen={() => setWeatherOpen(true)} />
          {device && <AIDailyReport device={device} farmName={farmName} input={reportInput} />}
          {showGrowth && device && <GrowthReportCard device={device} />}
        </div>

        {/* controls: device + period */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px 8px 14px', borderRadius: 14,
            background: 'var(--md-sys-color-surface-container-lowest)', border: '1px solid var(--md-sys-color-outline-variant)',
          }}>
            <Sym name="sensors" size={18} style={{ color: 'var(--md-sys-color-primary)' }} />
            <span style={{ font: '600 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>장비 선택</span>
            <select
              value={effectiveDevId}
              onChange={(e) => setDevId(e.target.value)}
              style={{
                height: 38, padding: '0 12px', borderRadius: 10, cursor: 'pointer',
                border: '1px solid var(--md-sys-color-outline-variant)', background: 'var(--md-sys-color-surface-container-low)',
                font: '600 14px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', outline: 'none', minWidth: 280,
              }}
            >
              <DeviceOptions devices={devices} farms={farms} />
            </select>
          </div>

          {/* 통계 단위 */}
          <div style={{ display: 'inline-flex', padding: 4, borderRadius: 9999, background: 'var(--md-sys-color-surface-container-high)' }}>
            {BUCKETS.map((b) => {
              const active = bucket === b;
              return (
                <button
                  key={b}
                  onClick={() => setBucket(b)}
                  style={{
                    height: 34, padding: '0 18px', borderRadius: 9999, border: 0, cursor: 'pointer',
                    background: active ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
                    color: active ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                    font: `${active ? 700 : 500} 13px/1 var(--gs-font-kr)`,
                    boxShadow: active ? 'var(--md-sys-elevation-1)' : 'none',
                  }}
                >
                  {BUCKET_LABEL[b]}
                </button>
              );
            })}
          </div>

          {/* 기간 이동 — 이전/다음 기간, 현재 기간으로 복귀. 미래로는 이동 불가 */}
          <PeriodNav
            label={periodLabel(bucket, anchor)}
            atLatest={atLatest}
            onPrev={() => setAnchor((a) => shiftAnchor(bucket, a, -1))}
            onNext={() => setAnchor((a) => shiftAnchor(bucket, a, 1))}
            onToday={() => setAnchor(new Date())}
          />

          {/* 통계/원본 토글 — 차트·원본 데이터 표에 동시 적용 */}
          <AggRawToggle mode={dataMode} onChange={setDataMode} />

          <span style={{ flex: 1 }} />

          {/* device meta */}
          {device && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <StatusPill s={uiStatus} />
              <span style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                {farmName} · {region}
              </span>
              <ModelTag m={device.model ?? 1} />
            </div>
          )}
        </div>

        {!device ? (
          <Panel>
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--md-sys-color-on-surface-variant)' }}>
              <Sym name="sensors_off" size={40} />
              <div style={{ font: '500 14px/20px var(--gs-font-kr)', marginTop: 10 }}>등록된 장비가 없습니다</div>
            </div>
          </Panel>
        ) : seriesError ? (
          <Panel><PageError message="센서 데이터를 불러오지 못했습니다." /></Panel>
        ) : (
          <>
            {/* 오프라인 안내 — 데이터는 과거 이력임을 명시 */}
            {isOff && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 14,
                background: 'var(--md-sys-color-surface-container-high)', border: '1px solid var(--md-sys-color-outline-variant)',
                color: 'var(--md-sys-color-on-surface-variant)', font: '500 13px/18px var(--gs-font-kr)',
              }}>
                <Sym name="cloud_off" size={18} />
                현재 오프라인 장비입니다. 아래는 마지막 수신{device.lastSeenAt ? ` (${fmtFullTime(device.lastSeenAt, false)})` : ''}까지의 과거 데이터입니다.
              </div>
            )}

            {/* chart */}
            <Panel
              style={{ flexShrink: 0 }}
              title={`${selected.map((s) => METRIC_DEFS[s].label).join(' · ')} 추이`}
              subtitle={
                dataMode === 'raw'
                  ? `${periodLabel(bucket, anchor)} · 개별 측정 ${chartRows.length.toLocaleString('ko-KR')}건 · ${device.name}`
                  : `${BUCKET_LABEL[bucket]} · ${periodLabel(bucket, anchor)} · ${device.name}`
              }
            >
              {/* 지표 선택 — 장비 보유 센서 전체, 최대 2개 겹쳐보기 (좌/우 축) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                {tableSensors.map((s) => {
                  const m = METRIC_DEFS[s];
                  const idx = selected.indexOf(s);
                  const active = idx >= 0;
                  return (
                    <button
                      key={s}
                      onClick={() => setSelected((prev) => toggleSelected(prev, s))}
                      style={{
                        height: 32, padding: '0 12px', borderRadius: 9999, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        border: active ? `1.5px solid ${m.color}` : '1px solid var(--md-sys-color-outline-variant)',
                        background: active ? `color-mix(in srgb, ${m.color} 12%, transparent)` : 'transparent',
                        color: active ? 'var(--md-sys-color-on-surface)' : 'var(--md-sys-color-on-surface-variant)',
                        font: `${active ? 600 : 500} 12px/1 var(--gs-font-kr)`,
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 9999, background: active ? m.color : 'var(--md-sys-color-outline)' }} />
                      {m.label}
                      {active && selected.length > 1 && (
                        <span style={{
                          font: '600 10px/1 var(--gs-font-kr)', padding: '2px 5px', borderRadius: 6,
                          background: m.color, color: '#fff',
                        }}>{idx === 0 ? '좌' : '우'}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {chartLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '200px 0' }}><LoadingSpinner /></div>
              ) : !chartHasData ? (
                <div style={{ padding: '200px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  선택한 기간에 데이터가 없습니다.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 14, alignItems: 'stretch' }}>
                  {/* 좌측: 선택 지표 통계(평균·최대·최소·현재)를 그래프 세로 높이에 맞춰 분배 */}
                  <div style={{
                    width: 168, flexShrink: 0, display: 'flex', flexDirection: 'column',
                    padding: '16px 14px', borderRadius: 16,
                    background: 'var(--md-sys-color-surface-container-low)',
                    border: '1px solid var(--md-sys-color-outline-variant)',
                  }}>
                    {selected.map((s, i) => {
                      const m = METRIC_DEFS[s];
                      const st = aggregate((chartSeriesData[s] ?? []).map((d) => d.v));
                      return (
                        <div
                          key={s}
                          style={{
                            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 9,
                            ...(i > 0
                              ? { borderTop: '1px solid var(--md-sys-color-outline-variant)', paddingTop: 14, marginTop: 14 }
                              : {}),
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 9999, background: m.color, flexShrink: 0 }} />
                            <span style={{ font: '600 13px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{m.label}</span>
                          </div>
                          <StatLine label="평균" value={fmtMetric(st.avg, m.decimals)} unit={m.unit} />
                          <StatLine label="최대" value={fmtMetric(st.max, m.decimals)} unit={m.unit} />
                          <StatLine label="최소" value={fmtMetric(st.min, m.decimals)} unit={m.unit} />
                          <StatLine label="현재" value={fmtMetric(st.cur, m.decimals)} unit={m.unit} big />
                        </div>
                      );
                    })}
                  </div>
                  {/* 우측: 추이 그래프 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <MetricChart
                      rows={chartRows}
                      series={chartSeries}
                      breakGaps={dataMode === 'agg'}
                      tickFmt={(iso) => (dataMode === 'raw' ? fmtTimeLabelRaw(iso, bucket !== 'hour') : bucketTick(bucket, iso))}
                      labelFmt={(iso) => (dataMode === 'raw' ? fmtFullTimeSec(iso) : bucketFull(bucket, iso))}
                    />
                  </div>
                </div>
              )}
            </Panel>

            {/* table */}
            <Panel
              style={{ flexShrink: 0 }}
              title="원본 데이터"
              subtitle={
                dataMode === 'raw'
                  ? `개별 측정값 · 총 ${rawTotal.toLocaleString('ko-KR')}건 · ${rawWideRows.length}개 측정 시각${rawTotalPages > 1 ? ` · ${rawPage}/${rawTotalPages} 페이지` : ''}`
                  : `${BUCKET_LABEL[bucket]} 통계 평균 · ${tableRows.length}개 측정 지점 · ${tableSensors.length}개 항목${aggTotalPages > 1 ? ` · ${safeAggPage}/${aggTotalPages} 페이지` : ''}`
              }
              pad={false}
              action={
                <button
                  onClick={exportCSV}
                  disabled={csvDisabled}
                  style={{
                    height: 34, padding: '0 14px', borderRadius: 9999,
                    cursor: csvDisabled ? 'default' : 'pointer',
                    background: 'transparent', border: '1px solid var(--md-sys-color-outline)',
                    color: csvDisabled ? 'var(--md-sys-color-outline)' : 'var(--md-sys-color-on-surface)',
                    font: '600 13px/1 var(--gs-font-kr)', display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Sym name="download" size={17} />CSV 내보내기
                </button>
              }
            >
              {dataMode === 'raw' ? (
                <>
                <div style={{ height: RAW_TABLE_HEIGHT, overflowY: 'scroll', overflowX: 'auto' }}>
                  <div style={{ minWidth: tableMinWidth }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: tableGridCols, position: 'sticky', top: 0, zIndex: 1,
                      background: 'var(--md-sys-color-surface-container-low)', borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                    }}>
                      <span style={{
                        padding: '12px 20px', font: '600 12px/16px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)', textAlign: 'left',
                      }}>시각</span>
                      {tableSensors.map((s) => (
                        <span key={s} style={{
                          padding: '12px 20px', font: '600 12px/16px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface-variant)', textAlign: 'right', whiteSpace: 'nowrap',
                        }}>{METRIC_DEFS[s].label} {METRIC_DEFS[s].unit}</span>
                      ))}
                    </div>
                    {rawQ.isLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}><LoadingSpinner /></div>
                    ) : rawQ.isError ? (
                      <div style={{ padding: '40px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-error)' }}>
                        원본 데이터를 불러오지 못했습니다.
                      </div>
                    ) : rawWideRows.length === 0 ? (
                      <div style={{ padding: '40px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                        선택한 기간에 측정 데이터가 없습니다.
                      </div>
                    ) : rawWideRows.map((r, i) => (
                      <div key={r.ts} style={{
                        display: 'grid', gridTemplateColumns: tableGridCols,
                        borderBottom: i === rawWideRows.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                        background: i % 2 ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
                      }}>
                        <span className="gs-num" style={{ padding: '11px 20px', font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', whiteSpace: 'nowrap' }}>
                          {fmtFullTimeSec(r.ts)}
                        </span>
                        {tableSensors.map((s) => {
                          const m = METRIC_DEFS[s];
                          const cell = r.cells[s];
                          const isAnom = cell != null && cell.anomaly !== AnomalyLevel.NORMAL;
                          return (
                            <span key={s} className="gs-num" style={{
                              padding: '11px 20px', textAlign: 'right',
                              font: `${isAnom ? 700 : 500} 13px/18px var(--gs-font-kr)`,
                              color: isAnom ? anomalyMeta(cell.anomaly).color : 'var(--md-sys-color-on-surface)',
                            }}>{cell == null ? '—' : fmtMetric(cell.v, m.decimals)}</span>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                {rawTotalPages > 1 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    padding: '12px 0', borderTop: '1px solid var(--md-sys-color-outline-variant)',
                  }}>
                    <RawPageBtn icon="chevron_left" disabled={rawPage <= 1} onClick={() => setRawPage(rawPage - 1)} />
                    <span className="gs-num" style={{ font: '600 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                      {rawPage} / {rawTotalPages}
                    </span>
                    <RawPageBtn icon="chevron_right" disabled={rawPage >= rawTotalPages} onClick={() => setRawPage(rawPage + 1)} />
                  </div>
                )}
                </>
              ) : (
                <>
                <div style={{ height: RAW_TABLE_HEIGHT, overflowY: 'scroll', overflowX: 'auto' }}>
                  <div style={{ minWidth: tableMinWidth }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: tableGridCols, position: 'sticky', top: 0, zIndex: 1,
                      background: 'var(--md-sys-color-surface-container-low)', borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                    }}>
                      <span style={{
                        padding: '12px 20px', font: '600 12px/16px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)', textAlign: 'left',
                      }}>시각</span>
                      {tableSensors.map((s) => (
                        <span key={s} style={{
                          padding: '12px 20px', font: '600 12px/16px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface-variant)', textAlign: 'right', whiteSpace: 'nowrap',
                        }}>{METRIC_DEFS[s].label} {METRIC_DEFS[s].unit}</span>
                      ))}
                    </div>
                    {tableRows.length === 0 ? (
                      <div style={{ padding: '40px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                        데이터가 없습니다.
                      </div>
                    ) : pagedTableRows.map((r, i) => (
                      <div key={r.ts} style={{
                        display: 'grid', gridTemplateColumns: tableGridCols,
                        borderBottom: i === pagedTableRows.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                        background: i % 2 ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
                      }}>
                        <span className="gs-num" style={{ padding: '11px 20px', font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', whiteSpace: 'nowrap' }}>
                          {bucketFull(bucket, r.ts)}
                        </span>
                        {tableSensors.map((s) => {
                          const m = METRIC_DEFS[s];
                          const val = r[s];
                          const warn = val != null && m.warn(val);
                          return (
                            <span key={s} className="gs-num" style={{
                              padding: '11px 20px', textAlign: 'right',
                              font: `${warn ? 700 : 500} 13px/18px var(--gs-font-kr)`,
                              color: warn ? 'var(--gs-warning)' : 'var(--md-sys-color-on-surface)',
                            }}>{val == null ? '—' : fmtMetric(val, m.decimals)}</span>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                {aggTotalPages > 1 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    padding: '12px 0', borderTop: '1px solid var(--md-sys-color-outline-variant)',
                  }}>
                    <RawPageBtn icon="chevron_left" disabled={safeAggPage <= 1} onClick={() => setAggPage(safeAggPage - 1)} />
                    <span className="gs-num" style={{ font: '600 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                      {safeAggPage} / {aggTotalPages}
                    </span>
                    <RawPageBtn icon="chevron_right" disabled={safeAggPage >= aggTotalPages} onClick={() => setAggPage(safeAggPage + 1)} />
                  </div>
                )}
                </>
              )}
            </Panel>
          </>
        )}
      </ViewScroll>

      {weatherOpen && weather && (
        <WeatherDetailModal snapshot={weather} regionName={region} onClose={() => setWeatherOpen(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 표 공통 상수
// ---------------------------------------------------------------------------

// 원본 데이터 표 영역 기본 세로 높이 (px). 내용이 넘치면 내부에서 세로 스크롤.
// 차트(그래프)는 별도 Panel + 자체 고정 높이라 이 값에 영향받지 않는다.
const RAW_TABLE_HEIGHT = 800;

// 통계 표 한 페이지당 행 수 (클라이언트 페이지네이션). 초과분은 페이지로 분할.
const AGG_PAGE_SIZE = 100;

// 원본 차트에 불러올 최대 측정 포인트 수. 초과분은 잘림(최신 우선은 서버 정렬에 따름).
// API의 pageSize 상한(500)을 넘으면 요청이 400으로 거부되므로 상한에 맞춘다.
const CHART_RAW_PAGE_SIZE = 500;

function RawPageBtn({ icon, disabled, onClick }: { icon: string; disabled: boolean; onClick: () => void }): React.ReactElement {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 34, height: 34, borderRadius: 9999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid var(--md-sys-color-outline-variant)', background: 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--md-sys-color-outline)' : 'var(--md-sys-color-on-surface)',
      }}
    >
      <Sym name={icon} size={20} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// 장비 옵션 (농장별 optgroup)
// ---------------------------------------------------------------------------

function DeviceOptions({ devices, farms }: { devices: DeviceInfo[]; farms: FarmInfo[] }): React.ReactElement {
  // 농장별 그룹 + 미할당 그룹
  const byFarm = new Map<string, DeviceInfo[]>();
  const unassigned: DeviceInfo[] = [];
  for (const d of devices) {
    if (d.farmId) {
      const list = byFarm.get(d.farmId) ?? [];
      list.push(d);
      byFarm.set(d.farmId, list);
    } else {
      unassigned.push(d);
    }
  }
  const opt = (d: DeviceInfo): React.ReactElement => (
    <option key={d.id} value={d.id}>
      {d.name} · {d.id.slice(0, 8)}{toUiStatus(d.status) === 'off' ? ' (오프라인)' : ''}
    </option>
  );
  return (
    <>
      {farms.map((f) => {
        const list = byFarm.get(f.id);
        if (!list?.length) return null;
        return <optgroup key={f.id} label={f.name}>{list.map(opt)}</optgroup>;
      })}
      {unassigned.length > 0 && <optgroup label="미할당">{unassigned.map(opt)}</optgroup>}
    </>
  );
}
